import React from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Edit, Trash2, ToggleLeft, ToggleRight } from "lucide-react"
import { type TaskFormData as Task, type TaskFormArrayData } from "@/lib/schemas"
import { useLanguage } from "../../contexts/language-context"
import { type FieldErrors } from "react-hook-form"

interface TasksTableProps {
  tasks: Task[]
  onEditTask: (task: Task) => void
  onRemoveTask: (id: number) => void
  onTaskToggleStatus: (task: Task) => void
  isReadonly?: boolean
  isChangeRequest?: boolean
  isMilestoneBilling?: boolean
  formErrors?: FieldErrors<TaskFormArrayData>
}

export const TasksTable = ({
  tasks,
  onEditTask,
  onRemoveTask,
  onTaskToggleStatus,
  isReadonly = false,
  isChangeRequest = false,
  isMilestoneBilling = false,
  formErrors
}: TasksTableProps) => {
  const { dictionary } = useLanguage()
  if (tasks.length === 0) {
    return (
      <div className="border rounded-md p-6 text-center text-muted-foreground bg-muted/10">
        {dictionary?.engagement?.tasks?.noTasksAdded || "No tasks have been added yet."}
      </div>
    )
  }

  return (
    <div className="border rounded-md overflow-hidden shadow-sm">
      <table className="w-full">
        <thead>
          <tr className="border-b bg-muted/30">
            <th className="py-3 px-4 text-left font-medium text-sm">
              {dictionary?.engagement?.tasks?.taskName || "Task Name"}
            </th>
            <th className="py-3 px-4 text-left font-medium text-sm">
              {isMilestoneBilling
                ? (dictionary?.engagement?.tasks?.isUtilized || "Is Utilized")
                : (dictionary?.engagement?.tasks?.billable || "Billable")}
            </th>
            <th className="py-3 px-4 text-left font-medium text-sm">
              {dictionary?.engagement?.core?.actions || "Actions"}
            </th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task, index) => (
            <tr key={task.id} className="border-b hover:bg-muted/10">
              <td className="py-3 px-4">
                <div className="flex items-center gap-2">
                  <span>
                    {task.name || (
                      <span className="text-muted-foreground italic">
                        {dictionary?.engagement?.tasks?.taskNamePlaceholder || "Task name not set"}
                      </span>
                    )}
                    {formErrors?.tasks?.[index] && <span className="text-destructive"> *</span>}
                  </span>
                  {task.inactive && (
                    <Badge variant="secondary" className="text-xs py-0 px-1 h-5">
                      {dictionary?.engagement?.tasks?.inactive || "Inactive"}
                    </Badge>
                  )}
                </div>
              </td>
              <td className="py-3 px-4">
                {isMilestoneBilling ? (
                  <Badge variant={task.isUtilized !== false ? "default" : "secondary"}>
                    {task.isUtilized !== false
                      ? (dictionary?.engagement?.tasks?.utilized || "Yes")
                      : (dictionary?.engagement?.tasks?.notUtilized || "No")}
                  </Badge>
                ) : (
                  <Badge variant={task.billable ? "default" : "secondary"}>
                    {task.billable
                      ? dictionary?.engagement?.tasks?.billable || "Billable"
                      : dictionary?.engagement?.tasks?.nonBillable || "Non-Billable"}
                  </Badge>
                )}
              </td>
              <td className="py-3 px-4">
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-blue-600 hover:bg-blue-50"
                    disabled={isReadonly}
                    onClick={() => onEditTask(task)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    {dictionary?.engagement?.core?.edit || "Edit"}
                  </Button>
                  {isChangeRequest ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-600 hover:bg-slate-50"
                      disabled={isReadonly}
                      onClick={() => onTaskToggleStatus(task)}
                    >
                      {task.inactive ? (
                        <>
                          <ToggleRight className="h-4 w-4 mr-2" />
                          Re-activate
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="h-4 w-4 mr-2" />
                          Deactivate
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:bg-destructive/10"
                      disabled={isReadonly}
                      onClick={() => onRemoveTask(task.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {dictionary?.engagement?.core?.delete || "Delete"}
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default TasksTable 